import DockingViewState from '../store/schema/DockingViewState';
import getScrollTopForCursor from '../utils/getScrollTopForCursor';
import { PluginViewStateAdapter } from 'owa-editor';
import ScrollAdapter from 'owa-scroll-adapter/lib/ScrollAdapter';
import { Editor, EditorPlugin } from 'roosterjs-editor-core';
import { ContentChangedEvent, PluginEvent, PluginEventType } from 'roosterjs-editor-types';

export interface VisibleAreaInfo {
    top: number;
    bottom: number;
    visibleBodyWidth: number;
    visibleBodyHeight: number;
}

export default abstract class DockingPluginBase implements EditorPlugin {
    protected editor: Editor;

    // +----------------------------------------------+----+
    // |  scrollContainer                             |  ^ |
    // |                                              |  # |
    // |  The scrollable container provides scroll bar|  # |
    // |                                              |  # |
    // |                                              |  # |
    // |   +------------------------------------+     |    |
    // |   |  initialVisiblePart (Optional)     |     |    |
    // |   |                                    |     |    |
    // |   |  When set, will scroll this part   |     |    |
    // |   |  into view initially               |     |    |
    // |   |                                    |     |    |
    // |   |   +---------------------------+    |     |    |
    // |   |   |  dockingTriggerPart       |    |     |    |
    // |   |   |                           |    |     |    |
    // |   |   |  When top or bottom of    |    |     |    |
    // |   |   |  this part is not visible |    |     |    |
    // |   |   |  it will trigger top or   |    |     |    |
    // |   |   |  bottom docking happen    |    |     |    |
    // |   |   |                           |    |     |    |
    // |   |   |                           |    |     |    |
    // |   |   +---------------------------+    |     |    |
    // |   |                                    |     |    |
    // |   |                                    |     |    |
    // |   +------------------------------------+     |    |
    // |                                              |    |
    // |                                              |  v |
    // +---------------------------------------------------+
    private initialVisiblePart: HTMLDivElement;
    protected dockingTriggerPart: HTMLDivElement;

    private scrollThrottlingJob: number;
    protected scrollAdapter: ScrollAdapter;

    constructor(protected adapter: PluginViewStateAdapter<DockingViewState>) {
        this.scrollAdapter = new ScrollAdapter();
    }

    public abstract getName(): string;

    public initialize(editor: Editor) {
        this.editor = editor;
        this.scrollAdapter.addScrollContainerChangedListener(this.onScrollContainerChanged);
    }

    public onPluginEvent(event: PluginEvent) {
        if (
            event.eventType == PluginEventType.KeyUp ||
            event.eventType == PluginEventType.KeyPress ||
            (event.eventType == PluginEventType.ContentChanged &&
                (<ContentChangedEvent>event).source != 'SetContent')
        ) {
            if (this.adapter().isBottomDocking) {
                this.scrollCursorIntoView();
            }
        }
    }

    private scrollCursorIntoView() {
        if (!this.scrollThrottlingJob) {
            this.scrollThrottlingJob = window.setTimeout(() => {
                this.scrollThrottlingJob = 0;
                const scrollContainer = this.scrollAdapter.getScrollContainer();
                if (scrollContainer && this.editor) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const cursorRect = this.editor.getCursorRect();
                    // editor.getCursorRect could return a null, make sure to check it before proceeding
                    if (containerRect && cursorRect) {
                        scrollContainer.scrollTop = getScrollTopForCursor(
                            this.adapter(),
                            scrollContainer.scrollTop,
                            containerRect.top,
                            containerRect.bottom,
                            cursorRect.top,
                            cursorRect.bottom
                        );
                    }
                }
            }, 100);
        }
    }

    public setDockingTriggerPart(dockingTriggerPart: HTMLDivElement) {
        this.dockingTriggerPart = dockingTriggerPart;
    }

    public setInitialVisiblePart(initialVisiblePart: HTMLDivElement) {
        this.initialVisiblePart = initialVisiblePart;
        this.tryScrollIntoView();
    }

    public getScrollAdapter() {
        return this.scrollAdapter;
    }

    public dispose() {
        this.scrollAdapter.removeScrollContainerChangedListener(this.onScrollContainerChanged);
        this.editor = null;
    }

    /**
     * Used when the content size where compose is hosted is changed
     * Most likely a compensate scroll is needed to ensure compose is in reasonable position in the scroll region
     * delta: the amount of size increased or decreased. 0 indicates no change.
     * sourceElement: the source of change. Also the field of interest that needs to be brought to view port
     * if there is no need to bring a source Element to view port, caller should pass in a null
     */
    public onContentSizeChanged(delta: number, sourceElement: HTMLElement = null) {
        const scrollContainer = this.scrollAdapter.getScrollContainer();

        if (!scrollContainer || delta == 0) {
            // scrollContainer is null, or there isn't really change
            return;
        }

        // Start with a min scrollTop of 0, and max allowed scroll top
        let minScrollTop = 0;
        let maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;

        // Calcuate the scroll top that is needed to keep compose in its original position
        let newScrollTop = scrollContainer.scrollTop + delta;
        newScrollTop = Math.max(minScrollTop, newScrollTop);
        newScrollTop = Math.min(newScrollTop, maxScrollTop);

        // sourceElement is not only the source where the content size is changed, but also
        // the field of interest which we need to ensure it is brought to view port
        // calculate the min & max scroll top required to bring source element to view port
        if (sourceElement) {
            minScrollTop = Math.max(
                minScrollTop,
                sourceElement.offsetTop + sourceElement.clientHeight - scrollContainer.clientHeight
            );
            maxScrollTop = Math.min(maxScrollTop, sourceElement.offsetTop);
        }

        // Now check if newScrollTop can fall in the scope and use it if so
        // Otherwise scroll to make sure sourceElemen is brought to view port
        if (
            newScrollTop != scrollContainer.scrollTop &&
            (newScrollTop >= minScrollTop && newScrollTop <= maxScrollTop)
        ) {
            scrollContainer.scrollTop = newScrollTop;
        } else {
            // When a scroll down is needed, pick the minScrollTop
            // Otherwise (scroll up), pick the maxScrollTop
            const originalScrollTop = scrollContainer.scrollTop;
            if (originalScrollTop < minScrollTop) {
                scrollContainer.scrollTop = minScrollTop;
            } else if (originalScrollTop > maxScrollTop) {
                scrollContainer.scrollTop = maxScrollTop;
            }
        }
    }

    public getVisibleAreaInfo(): VisibleAreaInfo {
        const scrollContainer = this.scrollAdapter.getScrollContainer();
        const viewState = this.adapter();

        let top: number = 0;
        let bottom: number = 0;
        let visibleBodyHeight: number;
        let visibleBodyWidth: number;

        if (
            scrollContainer &&
            scrollContainer.scrollHeight !== scrollContainer.clientHeight &&
            this.dockingTriggerPart
        ) {
            const scrollTop = scrollContainer.scrollTop;
            const clientHeight = scrollContainer.clientHeight;
            const offsetTop = this.dockingTriggerPart.offsetTop;
            const offsetHeight = this.dockingTriggerPart.offsetHeight;
            bottom = viewState.isBottomDocking
                ? offsetTop +
                  offsetHeight -
                  scrollTop -
                  clientHeight +
                  viewState.bottomDockingHeight
                : 0;
        }

        if (this.dockingTriggerPart) {
            visibleBodyHeight = this.dockingTriggerPart.offsetHeight - bottom;
        }

        visibleBodyWidth =
            viewState.dockingWidth ||
            (this.dockingTriggerPart ? this.dockingTriggerPart.offsetWidth : 0);

        return {
            top: top,
            bottom: bottom,
            visibleBodyHeight: visibleBodyHeight,
            visibleBodyWidth: visibleBodyWidth,
        };
    }

    protected tryScrollIntoView() {
        const scrollContainer = this.scrollAdapter.getScrollContainer();
        if (this.editor && scrollContainer && this.initialVisiblePart) {
            const viewport = scrollContainer.getBoundingClientRect();
            const inlineComposeRect = this.initialVisiblePart.getBoundingClientRect();

            if (inlineComposeRect.bottom > viewport.bottom) {
                scrollContainer.scrollTop += inlineComposeRect.bottom - viewport.bottom;
            }
            if (inlineComposeRect.top < viewport.top) {
                scrollContainer.scrollTop += inlineComposeRect.top - viewport.top;
            }
        }
    }

    protected abstract onScrollContainerChanged: (scrollContainer: HTMLDivElement) => void;
}
